// oxlint-disable typescript/no-unsafe-type-assertion

import { type ActorRefFrom, type MachineImplementationsFrom, setup } from 'xstate5';

import { Context, Event, Input } from './receipt.machine.types';
import { Actions } from './setup/receipt.machine.actions';
import { Actors } from './setup/receipt.machine.actors';
import { generateInitialContext } from './setup/receipt.machine.context';
import { Guards } from './setup/receipt.machine.guards';
import * as Inputs from './setup/receipt.machine.input';

// ─── Model ───────────────────────────────────────────────────────────────────

const model = setup({
  types: {
    input: {} as Input,
    context: {} as Context,
    events: {} as Event,
  },
  actions: Actions,
  actors: Actors,
  guards: Guards,
});

// ─────────────────────────────────────────────────────────────────────────────

export const receiptMachine = model.createMachine({
  initial: 'fetching-receipt',
  context: (args) => generateInitialContext(args.input),
  states: {
    'fetching-receipt': {
      invoke: {
        id: 'fetchReceipt',
        src: 'fetchReceipt',
        input: Inputs.getInitialInput,
        onDone: [
          {
            target: 'claimed',
            guard: {
              type: 'checkIfReceiptAlreadyClaimedByUser',
              params: (args) => args.event.output,
            },
            actions: [{ type: 'assignReceiptData', params: (args) => args.event.output }],
          },
          {
            target: 'claiming',
            guard: { type: 'checkIfReceipt', params: (args) => args.event.output },
            actions: [{ type: 'assignReceiptData', params: (args) => args.event.output }],
          },
          {
            target: 'error.expired',
            guard: { type: 'checkIfExpiredReceipt', params: (args) => args.event.output },
          },
          {
            target: 'error.not-found',
            guard: { type: 'checkIfReceiptNotFound', params: (args) => args.event.output },
          },
          {
            target: 'error.already-claimed',
            guard: { type: 'checkIfReceiptAlreadyClaimed', params: (args) => args.event.output },
          },
          {
            target: 'error.unknown-error',
          },
        ],
        onError: {
          target: 'error.unknown-error',
          actions: ['assignError'],
        },
      },
    },
    claiming: {
      invoke: {
        id: 'claimReceipt',
        src: 'claimReceipt',
        input: Inputs.getClaimReceiptInput,
        onDone: [
          {
            target: 'claimed',
            guard: { type: 'checkIfClaimSucceeded', params: (args) => args.event.output },
          },
          {
            target: 'error.already-claimed',
            guard: {
              type: 'checkIfReceiptAlreadyClaimedError',
              params: (args) => args.event.output,
            },
          },
          {
            target: 'error.time-restricted',
            guard: { type: 'checkIfTimeRestrictionError', params: (args) => args.event.output },
          },
          {
            target: 'error.not-found',
            guard: {
              type: 'checkIfTransactionNotFoundError',
              params: (args) => args.event.output,
            },
          },
          {
            target: 'error.unknown-error',
          },
        ],
        onError: {
          target: 'error.unknown-error',
          actions: 'assignError',
        },
      },
    },

    // ─── Final States ────────────────────────────────────────────────

    claimed: {
      type: 'final',
      entry: ['onSuccessfulClaim', 'logSuccessfulClaim'],
    },

    error: {
      initial: 'unknown-error',
      states: {
        expired: {
          type: 'final',
          entry: [
            'onExpired',
            { type: 'logFailedClaimDueToUserError', params: { errorType: 'expired' } },
          ],
        },
        'time-restricted': {
          type: 'final',
          entry: [
            'onTimeRestriction',
            { type: 'logFailedClaimDueToUserError', params: { errorType: 'time-restricted' } },
          ],
        },
        'already-claimed': {
          type: 'final',
          entry: [
            'onAlreadyClaimed',
            { type: 'logFailedClaimDueToUserError', params: { errorType: 'already-claimed' } },
          ],
        },
        'not-found': {
          type: 'final',
          entry: [
            'onNotFound',
            { type: 'logFailedClaimDueToUserError', params: { errorType: 'not-found' } },
          ],
        },
        'unknown-error': {
          type: 'final',
          entry: ['onUnknownError', 'logFailedClaim'],
        },
      },
    },
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

export type ReceiptMachineActorRef = ActorRefFrom<typeof receiptMachine>;
export type ReceiptMachineImplementations = MachineImplementationsFrom<typeof receiptMachine>;
